import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Autocomplete,
  TableCell,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonIcon,
  IonSearchbar,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from "@ionic/react";

type Steward = {
  id: string;
  user_id?: string; // Add this if your API returns `user_id`
  firstName: string;
  lastName: string;
  fullname?: string;
};

const SalesDashboard = () => {
  const [stewards, setStewards] = useState<Steward[]>([]);
  const [orderBy, setOrderBy] = useState<Steward | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salesData, setSalesData] = useState<any[]>([]);

  useEffect(() => {
    const formData = new FormData();
    formData.append("requestID", requestID());

    axios
      .post(baseUrl() + "staffs/getStaffs", formData, config)
      .then((res) => {
        console.log("Fetched Stewards:", res.data);

        if (Array.isArray(res.data)) {
          setStewards(res.data);
        } else if (Array.isArray(res.data.data)) {
          setStewards(res.data.data);
        } else {
          console.warn("Unexpected steward data structure");
          setStewards([]);
        }
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch stewards");
      });
  }, []);

  const getSalesReport = () => {
    if (!orderBy || !startDate || !endDate) {
      alert("Please fill all filters");
      return;
    }

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("order_by", orderBy.user_id || orderBy.id); // fallback to id if user_id is missing
    formData.append("start_date", startDate);
    formData.append("end_date", endDate);

    axios
      .post(baseUrl() + "orderhistorys/orderReport", formData, config)
      .then((res) => {
        console.log("Sales Data:", res.data);

        const orders = res.data.data || [];
        const allItems = orders.flatMap((order: any) =>
          order.items.map((item: any) => ({
            ...item,
            order_code: order.order_code,
            order_date: order.order_date,
            mode_of_payment: order.mode_of_payment,
          }))
        );

        setSalesData(allItems);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch sales report");
      });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Sales Report</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: "2rem" }}>
          <Typography variant="h4" gutterBottom>
            Sales Report Dashboard
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={stewards}
                value={orderBy}
                onChange={(event, newValue) => setOrderBy(newValue)}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.user_id === value.user_id || option.id === value.id
                }
                renderInput={(params) => (
                  <TextField {...params} label="Select Steward" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" fullWidth onClick={getSalesReport}>
                Get Report
              </Button>
            </Grid>
          </Grid>

          <div style={{ marginTop: "2rem" }}>
            {salesData.length > 0 ? (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>
                        <strong>Product</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Quantity</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Price</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Payment</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                        }}
                      >
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.price}</TableCell>
                        <TableCell>{item.total_amount}</TableCell>
                        <TableCell>{item.mode_of_payment}</TableCell>
                        <TableCell>{item.order_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" sx={{ mt: 4 }}>
                No sales data yet.
              </Typography>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SalesDashboard;
